import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navigation from './components/Navigation';
import ToastContainer from './components/Toast';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DriverRegisterPage from './pages/DriverRegisterPage';
import HomePage from './pages/HomePage';
import CustomerDashboard from './pages/CustomerDashboard';
import DriverDashboard from './pages/DriverDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ProductsPage from './pages/ProductsPage';
import ProductManagement from './pages/ProductManagement';
import DriverManagement from './pages/DriverManagement';
import DriverProfile from './pages/DriverProfile';
import CustomerList from './pages/CustomerList';
import OrdersPage from './pages/OrdersPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import ProfilePage from './pages/ProfilePage';
import PickupEventsManagement from './pages/PickupEventsManagement';
import PickupEventDetail from './pages/PickupEventDetail';
import DeliveryDetail from './pages/DeliveryDetail';
import './App.css';

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
}

function AppContent() {
  const location = useLocation();
  const showNavigation = location.pathname !== '/';

  return (
    <div className="App">
      <ToastContainer />
      {showNavigation && <Navigation />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/register-driver" element={<DriverRegisterPage />} />
        <Route path="/products" element={<ProductsPage />} />

            {/* Customer Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={['customer']}>
                  <CustomerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute allowedRoles={['customer', 'admin']}>
                  <OrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute allowedRoles={['customer', 'admin']}>
                  <SubscriptionsPage />
                </ProtectedRoute>
              }
            />

            {/* Driver Routes */}
            <Route
              path="/driver"
              element={
                <ProtectedRoute allowedRoles={['driver']}>
                  <DriverDashboard />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ProductManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/drivers"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <DriverManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/drivers/:id"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <DriverProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/customers"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <CustomerList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pickup-events"
              element={
                <ProtectedRoute allowedRoles={['admin', 'driver']}>
                  <PickupEventsManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pickup-events/:id"
              element={
                <ProtectedRoute allowedRoles={['admin', 'driver']}>
                  <PickupEventDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/deliveries/:id"
              element={
                <ProtectedRoute allowedRoles={['admin', 'driver', 'customer']}>
                  <DeliveryDetail />
                </ProtectedRoute>
              }
            />

            {/* Common Routes */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
